import React, { useState, useRef } from 'react';
import {
    Box,
    TextField,
    Typography,
    Alert,
    Button,
    Paper,
    IconButton,
    Chip
} from '@mui/material';
import {
    AutoAwesome as AiIcon,
    Add as AddIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import QuizGenerator from '../QuizGenerator';
import RichTextInput from './RichTextInput';

interface FreeTextEditorProps {
    content: any;
    onChange: (content: any) => void;
}

interface FreeTextQuestion {
    id: string;
    question: string;
    sampleAnswer?: string;
    gradingCriteria: string[];
    minWords?: number;
    maxWords?: number;
    maxScore?: number;
}

const FreeTextEditor: React.FC<FreeTextEditorProps> = ({ content, onChange }) => {
    const [generatorOpen, setGeneratorOpen] = useState(false);
    const questions: FreeTextQuestion[] = content?.questions || [];

    const systemPrompt = content?.systemPrompt || '';

    const addQuestion = () => {
        const newQuestion: FreeTextQuestion = {
            id: Date.now().toString(),
            question: '',
            sampleAnswer: '',
            gradingCriteria: [],
            minWords: 0,
            maxWords: 0,
            maxScore: 100
        };
        onChange({ ...content, questions: [...questions, newQuestion] });
    };

    const updateQuestion = (questionId: string, field: string, value: any) => {
        const updatedQuestions = questions.map((q) =>
            q.id === questionId ? { ...q, [field]: value } : q
        );
        onChange({ ...content, questions: updatedQuestions });
    };

    const deleteQuestion = (questionId: string) => {
        const updatedQuestions = questions.filter((q) => q.id !== questionId);
        onChange({ ...content, questions: updatedQuestions });
    };

    const CriteriaInput: React.FC<{ questionId: string; criteria: string[] }> = ({ questionId, criteria }) => {
        const [input, setInput] = useState('');
        const inputRef = useRef<HTMLInputElement>(null);

        const addCriterion = () => {
            const trimmed = input.trim();
            if (!trimmed || criteria.includes(trimmed)) return;
            updateQuestion(questionId, 'gradingCriteria', [...criteria, trimmed]);
            setInput('');
            inputRef.current?.focus();
        };

        const removeCriterion = (index: number) => {
            updateQuestion(questionId, 'gradingCriteria', criteria.filter((_, i) => i !== index));
        };

        return (
            <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Bewertungskriterien
                </Typography>
                <Box
                    sx={{
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 1.5,
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1,
                        minHeight: 56,
                        cursor: 'text',
                        '&:focus-within': { borderColor: 'primary.main', borderWidth: 2 },
                    }}
                    onClick={() => inputRef.current?.focus()}
                >
                    {criteria.map((criterion, i) => (
                        <Chip
                            key={i}
                            label={criterion}
                            size="small"
                            onDelete={() => removeCriterion(i)}
                        />
                    ))}
                    <input
                        ref={inputRef}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') { e.preventDefault(); addCriterion(); }
                            if (e.key === 'Backspace' && !input && criteria.length > 0) {
                                removeCriterion(criteria.length - 1);
                            }
                        }}
                        placeholder={criteria.length === 0 ? 'Kriterium eingeben und Enter drücken...' : ''}
                        style={{
                            border: 'none',
                            outline: 'none',
                            flex: 1,
                            minWidth: 200,
                            fontSize: 14,
                            background: 'transparent',
                            padding: '2px 4px',
                        }}
                    />
                </Box>
                <Typography variant="caption" color="text.secondary">
                    Enter drücken um ein Kriterium hinzuzufügen · Backspace entfernt das letzte
                </Typography>
            </Box>
        );
    };

    const handleQuestionsGenerated = (generatedQuestions: any[]) => {
        const newQuestions: FreeTextQuestion[] = generatedQuestions.map((gq, index) => ({
            id: Date.now() + '-' + index,
            question: gq.question || '',
            sampleAnswer: gq.sampleAnswer || '',
            gradingCriteria: gq.gradingCriteria || [],
            minWords: 0,
            maxWords: 0,
            maxScore: 100
        }));
        onChange({ ...content, questions: [...questions, ...newQuestions] });
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Freitext-Aufgaben</Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="outlined"
                        startIcon={<AiIcon />}
                        onClick={() => setGeneratorOpen(true)}
                    >
                        KI-Generator
                    </Button>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={addQuestion}>
                        Frage hinzufügen
                    </Button>
                </Box>
            </Box>

            <Alert severity="info">
                Freitext-Aufgaben werden durch KI automatisch bewertet.
                Geben Sie für jede Frage klare Bewertungskriterien an.
            </Alert>

            <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                <Typography variant="subtitle2" gutterBottom>
                    Globale Einstellungen (gelten für alle Fragen)
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                    <TextField
                        label="System-Prompt für KI (optional)"
                        fullWidth
                        multiline
                        rows={3}
                        value={systemPrompt}
                        onChange={(e) => onChange({ ...content, systemPrompt: e.target.value })}
                        helperText="Erweiterte Einstellung: Spezielle Anweisungen für die KI"
                        placeholder="Du bist ein erfahrener Trainer, der die Antwort von Teilnehmenden bewertet..."
                    />
                </Box>
            </Paper>

            {/* Questions */}
            {questions.length === 0 ? (
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                    <Typography color="text.secondary">
                        Noch keine Fragen vorhanden. Klicken Sie auf "Frage hinzufügen" oder "KI-Generator" um zu beginnen.
                    </Typography>
                </Paper>
            ) : (
                questions.map((question, index) => (
                    <Paper key={question.id} sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Chip label={`Frage ${index + 1}`} color="primary" />
                            <IconButton
                                onClick={() => deleteQuestion(question.id)}
                                color="error"
                                size="small"
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <RichTextInput
                                label="Aufgabenstellung *"
                                value={question.question}
                                onChange={(html) => updateQuestion(question.id, 'question', html)}
                                placeholder="Beschreiben Sie, was der Lernende tun soll..."
                                minHeight={120}
                            />

                            <RichTextInput
                                label="Musterlösung (optional)"
                                value={question.sampleAnswer || ''}
                                onChange={(html) => updateQuestion(question.id, 'sampleAnswer', html)}
                                helperText="Eine Beispielantwort, an der sich die KI orientieren kann"
                                minHeight={150}
                            />

                            <CriteriaInput
                                questionId={question.id}
                                criteria={question.gradingCriteria || []}
                            />

                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
                                <TextField
                                    label="Min. Wörter"
                                    type="number"
                                    fullWidth
                                    value={question.minWords || 0}
                                    onChange={(e) => updateQuestion(question.id, 'minWords', parseInt(e.target.value) || 0)}
                                    inputProps={{ min: 0 }}
                                />

                                <TextField
                                    label="Max. Wörter"
                                    type="number"
                                    fullWidth
                                    value={question.maxWords || 0}
                                    onChange={(e) => updateQuestion(question.id, 'maxWords', parseInt(e.target.value) || 0)}
                                    helperText="0 = unbegrenzt"
                                    inputProps={{ min: 0 }}
                                />

                                <TextField
                                    label="Maximale Punktzahl"
                                    type="number"
                                    fullWidth
                                    value={question.maxScore || 100}
                                    onChange={(e) => updateQuestion(question.id, 'maxScore', parseInt(e.target.value) || 100)}
                                    inputProps={{ min: 1 }}
                                />
                            </Box>

                        </Box>
                    </Paper>
                ))
            )}

            <QuizGenerator
                open={generatorOpen}
                onClose={() => setGeneratorOpen(false)}
                onQuestionsGenerated={handleQuestionsGenerated}
                defaultQuestionType="free_text"
            />
        </Box>
    );
};

export default FreeTextEditor;
